import {
	type CellContext,
	type Column,
	type Row,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table";
import React, { type FC, useState } from "react";
import { useIntersection } from "react-use";

import { cx } from "~/tools/class-variance";
import type { Record } from "~/tools/record";
import { useVirtual } from "~/tools/use-virtual";

import { Api } from "../../../../../../common/openapi";
import { useClosestScrollingContainer } from "../../../../../../components/_scrolling-container";
import { NoMoreScrollToTop } from "../../../../../../components/no-more-scroll-to-top";
import { RadioButton, RadioButtonGroup } from "../../../../../../components/react/buttons/RadioButton";
import { GenericErrorMessage } from "../../../../../../components/react/error/GenericErrorMessage";
import { LoadingSpinner } from "../../../../../../components/react/loading-spinner/LoadingSpinner";
import { Switch } from "../../../../../../components/react/switch/Switch";
import { ScrollToTop } from "../../../../../../components/scroll-to-top";

import { type CellContextWithExtraOptions, logsDrawerColumns } from "./ElementPropertiesLogsDrawerColumns";

// height of top items: filters, histogram and table options
const heightOfContentAboveTable = 233;

export interface TableDataProps {
	data: Array<Api.KubernetesLogRecord>;
	hasNextPage: boolean;
	isError: boolean;
	error?: any;
	refetch?: () => void;
	isFetching: boolean;
	isFetchingNextPage: boolean;
	isLoading: boolean;
	order: Api.LogsDirection;
	setOrder: React.Dispatch<Api.LogsDirection>;
	totalDBRowCount: number;
}

export const ElementPropertiesLogsDrawerTable: FC<TableDataProps> = ({
	data,
	hasNextPage,
	isError,
	error,
	refetch,
	isFetching,
	isFetchingNextPage,
	isLoading,
	order,
	setOrder,
	totalDBRowCount,
}) => {
	const { element: scrollingContainer } = useClosestScrollingContainer();
	const intersectionBoundaryRef = React.useRef<HTMLDivElement>(null);
	const intersection = useIntersection(intersectionBoundaryRef, {
		threshold: 0,
	});
	const [columnVisibility, setColumnVisibility] = React.useState<Record<string, boolean>>({ containerName: false });
	const [wrapLines, setWrapLines] = useState(true);

	const containerIndexes = React.useMemo(() => {
		const acc = new Map<string, number>();

		for (const [index, { containerName }] of data.entries()) {
			if (!acc.has(containerName)) {
				acc.set(containerName, index);
			}
		}

		return acc;
	}, [data]);

	const table = useReactTable({
		data,
		columns: logsDrawerColumns,
		state: {
			columnVisibility,
		},
		onColumnVisibilityChange: setColumnVisibility,
		getCoreRowModel: getCoreRowModel(),
		// workaround to get custom width which is not a number https://github.com/TanStack/table/discussions/3192
		defaultColumn: {
			minSize: 0,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		debugTable: true,
	});

	const { rows } = table.getRowModel();

	const virtualizer = useVirtual({
		count: rows.length,
		getScrollElement: () => scrollingContainer,
		estimateSize: 23,
		scrollPaddingStart: heightOfContentAboveTable,
		overscan: 12,
		paddingEnd: heightOfContentAboveTable - 32, // subtract table container top and bottom padding
	});

	const items = virtualizer.getVirtualItems();

	return (
		<>
			{data.length > 0 && (
				<>
					<TableOptions
						columns={table.getAllLeafColumns()}
						order={[order, setOrder]}
						wrapLines={[wrapLines, setWrapLines]}
					/>

					<div ref={intersectionBoundaryRef} />

					<div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
						<div
							className="absolute top-0 left-0 w-full"
							style={{ transform: `translateY(${items[0]?.start || 0}px)` }}
						>
							<div
								className={cx(
									"overflow-x-auto overflow-y-hidden rounded-xs bg-white p-4 pl-1",
									isFetching && "border-b-0",
								)}
							>
								<table className="w-full border-separate border-spacing-y-[3px] overflow-hidden font-mono text-xs/[18px]">
									<tbody>
										{items.map((virtualRow, index) => {
											if (virtualRow.index == null || rows[virtualRow.index] == null) {
												// somehow virtualRow might be undefined here, so it is better to fallback to index in this case
												return <tr key={virtualRow.key ?? index} />;
											}

											const row = rows[virtualRow.index] as Row<Api.KubernetesLogRecord>;
											return (
												<tr
													className="relative overflow-hidden"
													data-index={virtualRow.index}
													key={virtualRow.key}
													ref={
														//TODO use typed virtualizer the way it can pass typescript check
														virtualizer.measureElement
													}
												>
													{row.getVisibleCells().map((cell) => (
														<td
															className={cx(
																"nth-[n+3]:pl-4 align-top first:block",
																wrapLines && cell.column.columnDef.meta?.enableWrap === true
																	? "break-on-word whitespace-normal"
																	: "whitespace-nowrap",
															)}
															key={cell.id}
															style={{
																width:
																	// if one of column is not on 100% we got column jumping, on scroll first column automatically change the size, it does not remain fixed
																	cell.column.getSize() === Number.MAX_SAFE_INTEGER ? "100%" : cell.column.getSize(),
															}}
														>
															{flexRender(
																cell.column.columnDef.cell,
																getCustomCellContext(cell.getContext(), wrapLines, containerIndexes),
															)}
														</td>
													))}
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</>
			)}

			<div className="pt-4 pb-8 text-center text-base text-grey-500 empty:hidden">
				{(isLoading || isFetchingNextPage) && <LoadingSpinner />}
				{!isFetching && totalDBRowCount > 0 && !hasNextPage && <NoMoreScrollToTop text="No more logs to display." />}
				{isError && (
					<GenericErrorMessage
						error={error}
						onRetry={refetch}
						text="Something went wrong while trying to fetch more logs."
					/>
				)}
			</div>

			<ScrollToTop className="fixed right-8 bottom-8" isVisible={intersection?.isIntersecting === false} />
		</>
	);
};

const getCustomCellContext = (
	cell: CellContext<Api.KubernetesLogRecord, unknown>,
	shouldWrapLines: boolean,
	containerIndexes: ReadonlyMap<string, number>,
): CellContextWithExtraOptions<unknown> => ({
	...cell,
	extraOptions: {
		shouldWrapLines,
		containerIndexes,
	},
});

interface TableOptionsProps {
	columns: Array<Column<Api.KubernetesLogRecord>>;
	wrapLines: [boolean, React.Dispatch<boolean>];
	order: [Api.LogsDirection, React.Dispatch<Api.LogsDirection>];
}

const TableOptions: FC<TableOptionsProps> = ({
	columns,
	wrapLines: [wrapLinesState, setWrapLines],
	order: [orderState, setOrder],
}) => {
	const getColumnById = (columnId: string) => columns.find((column) => column.id === columnId);

	const isColumnVisible = (columnId: string): boolean => {
		const column = getColumnById(columnId);
		return column ? column.getIsVisible() : false;
	};

	const toggleColumnVisibility = (columnId: string) => {
		const timeCol = getColumnById(columnId);
		if (timeCol) {
			timeCol.toggleVisibility();
		}
	};

	return (
		<div className="flex flex-wrap items-center justify-end gap-4" data-testid="pod-logs-table-options">
			<SwitchLabel data-testid="show-time-wrapper">
				Show time
				<Switch
					defaultChecked={isColumnVisible("timestamp")}
					onChange={() => toggleColumnVisibility("timestamp")}
					size="small"
				/>
			</SwitchLabel>

			<SwitchLabel data-testid="show-containers-wrapper">
				Show containers
				<Switch
					defaultChecked={isColumnVisible("containerName")}
					onChange={() => toggleColumnVisibility("containerName")}
					size="small"
				/>
			</SwitchLabel>

			<SwitchLabel data-testid="wrap-lines-wrapper">
				Wrap lines
				<Switch defaultChecked={wrapLinesState} onChange={(value) => setWrapLines(value)} size="small" />
			</SwitchLabel>

			<RadioButtonGroup onChange={setOrder} value={orderState}>
				<RadioButton value={Api.LogsDirection.NEWEST}>Newest first</RadioButton>
				<RadioButton value={Api.LogsDirection.OLDEST}>Oldest first</RadioButton>
			</RadioButtonGroup>
		</div>
	);
};

const SwitchLabel: React.FC<React.PropsWithChildren> = (props) => (
	<label {...props} className="flex cursor-pointer items-center gap-2 text-13" />
);
